import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="faults-form"
export default class extends Controller {
  static targets = ["faultItem", "customNoteInput", "customAdviceInput", "resolution"]

  connect() {
    this.boundHandleSubmitStart = this.handleSubmitStart.bind(this)
    this.boundHandleSubmitEnd = this.handleSubmitEnd.bind(this)
    this.element.addEventListener("turbo:submit-start", this.boundHandleSubmitStart)
    this.element.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd)
    this.element.querySelectorAll("textarea[data-faults-form-target]").forEach(el => this.#resize(el))
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this.boundHandleSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this.boundHandleSubmitEnd)
  }

  handleKeydown(event) {
    if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
      event.preventDefault()
      this.doSubmit()
    }
  }

  autoResize(event) {
    this.#resize(event.target)
  }

  // Mirror the faults list in the resolution select so it feels live. The
  // server still recalculates the final value on save.
  syncResolution() {
    if (!this.hasResolutionTarget) return

    this.resolutionTarget.value = this.#hasSelectedFaults() ? "fault" : "ok"
  }

  #hasSelectedFaults() {
    const anyChecked = this.faultItemTargets.some(item =>
      item.querySelector("input[type='checkbox']")?.checked
    )
    const customNote = this.hasCustomNoteInputTarget ? this.customNoteInputTarget.value.trim() : ""
    const customAdvice = this.hasCustomAdviceInputTarget ? this.customAdviceInputTarget.value.trim() : ""
    return anyChecked || Boolean(customNote || customAdvice)
  }

  #resize(el) {
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }

  doSubmit() {
    this.buildFaultInputs()
    this.element.requestSubmit()
  }

  buildFaultInputs() {
    this.element.querySelectorAll("[data-fault-injected]").forEach(el => el.remove())

    // Always include sentinel so server knows faults were explicitly submitted
    this.injectInput("device_check[faults_present]", "1")

    this.faultItemTargets.forEach(item => {
      const checkbox = item.querySelector("input[type='checkbox']")
      if (!checkbox?.checked) return

      this.injectInput("device_check[faults][][fault_id]", item.dataset.faultId)
      if (item.dataset.deviceFaultId) {
        this.injectInput("device_check[faults][][id]", item.dataset.deviceFaultId)
      }
    })

    if (this.hasCustomNoteInputTarget) {
      const noteInput = this.customNoteInputTarget
      const adviceInput = this.hasCustomAdviceInputTarget ? this.customAdviceInputTarget : null
      const note = noteInput.value.trim()
      const advice = adviceInput ? adviceInput.value.trim() : ""
      if (note || advice) {
        this.injectInput("device_check[faults][][fault_id]", noteInput.dataset.faultId)
        if (noteInput.dataset.deviceFaultId) {
          this.injectInput("device_check[faults][][id]", noteInput.dataset.deviceFaultId)
        }
        this.injectInput("device_check[faults][][note]", note)
        this.injectInput("device_check[faults][][advice]", advice)
      }
    }
  }

  injectInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value || ""
    input.dataset.faultInjected = "1"
    this.element.appendChild(input)
  }

  handleSubmitStart() {
    document.dispatchEvent(new CustomEvent("device-check:saving", { bubbles: true }))
  }

  handleSubmitEnd() {
    document.dispatchEvent(new CustomEvent("device-check:saved", { bubbles: true }))
    window.dispatchEvent(new Event("modal:close"))
  }
}
